// Real-time leads land in this queue only AFTER an external dialer has already
// made contact, so every lead carries a last-contacted timestamp. From here the
// job is conversion: a user claims the lead and has a fixed window to get a
// loan created. If the window lapses with no loan, the claim is released and the
// lead returns to the shared queue for someone else to work.

export type LeadState =
  | 'available'
  | 'claimed_by_me'
  | 'claimed_by_other'
  | 'loan_created'

/** How long a claim holds before the lead falls back to the shared queue. */
export const CLAIM_WINDOW_HOURS = 24

export interface LeadSource {
  id: string
  name: string
  /** Roughly what the company pays per lead from this source. */
  costPerLead: number
}

export interface RealtimeLead {
  id: string
  name: string
  phone: string
  email: string
  loanAmount: number
  loanPurpose: 'Purchase' | 'Refinance'
  state: string
  sourceId: string
  /** When the external dialer last spoke with the borrower. */
  lastContactedAt: string
  /** Outcome the dialer logged on that contact. */
  dialerOutcome: string
  claimedBy: string | null
  claimedByName: string | null
  claimedAt: string | null
  loanCreated: boolean
  /** Times this lead has cycled back to the queue after an expired claim. */
  releaseCount: number
}

export const LEAD_SOURCES: LeadSource[] = [
  { id: 'five9-transfer', name: 'Five9 Live Transfer', costPerLead: 145 },
  { id: 'lendingtree', name: 'LendingTree', costPerLead: 95 },
  { id: 'bankrate', name: 'Bankrate', costPerLead: 88 },
  { id: 'nerdwallet', name: 'NerdWallet', costPerLead: 76 },
  { id: 'google-lsa', name: 'Google LSA', costPerLead: 62 },
  { id: 'zillow', name: 'Zillow', costPerLead: 54 },
  { id: 'facebook', name: 'Facebook Ads', costPerLead: 28 },
  { id: 'website', name: 'Website Direct', costPerLead: 0 },
]

const hoursAgo = (h: number) =>
  new Date(Date.now() - h * 60 * 60 * 1000).toISOString()

export const REALTIME_LEADS: RealtimeLead[] = [
  {
    id: 'RT-2001',
    name: 'Derek Mullins',
    phone: '(555) 341-9920',
    email: 'd.mullins@example.com',
    loanAmount: 385000,
    loanPurpose: 'Refinance',
    state: 'FL',
    sourceId: 'five9-transfer',
    lastContactedAt: hoursAgo(0.4),
    dialerOutcome: 'Successful contact',
    claimedBy: null,
    claimedByName: null,
    claimedAt: null,
    loanCreated: false,
    releaseCount: 0,
  },
  {
    id: 'RT-2002',
    name: 'Priya Raman',
    phone: '(555) 662-1184',
    email: 'praman@example.com',
    loanAmount: 540000,
    loanPurpose: 'Purchase',
    state: 'TX',
    sourceId: 'lendingtree',
    lastContactedAt: hoursAgo(2.5),
    dialerOutcome: 'Warm transfer',
    claimedBy: null,
    claimedByName: null,
    claimedAt: null,
    loanCreated: false,
    releaseCount: 1,
  },
  {
    id: 'RT-2003',
    name: 'Anthony Barros',
    phone: '(555) 908-7741',
    email: 'abarros@example.com',
    loanAmount: 298000,
    loanPurpose: 'Refinance',
    state: 'NJ',
    sourceId: 'bankrate',
    lastContactedAt: hoursAgo(1.2),
    dialerOutcome: 'Successful contact',
    claimedBy: 'officer1',
    claimedByName: 'John Officer',
    claimedAt: hoursAgo(21.5),
    loanCreated: false,
    releaseCount: 0,
  },
  {
    id: 'RT-2004',
    name: 'Michelle Okafor',
    phone: '(555) 220-3390',
    email: 'm.okafor@example.com',
    loanAmount: 615000,
    loanPurpose: 'Purchase',
    state: 'GA',
    sourceId: 'nerdwallet',
    lastContactedAt: hoursAgo(5),
    dialerOutcome: 'Requested callback',
    claimedBy: 'officer2',
    claimedByName: 'Jane Officer',
    claimedAt: hoursAgo(3),
    loanCreated: false,
    releaseCount: 0,
  },
  {
    id: 'RT-2005',
    name: 'Grant Whitfield',
    phone: '(555) 774-5510',
    email: 'gwhitfield@example.com',
    loanAmount: 432000,
    loanPurpose: 'Refinance',
    state: 'AZ',
    sourceId: 'google-lsa',
    lastContactedAt: hoursAgo(0.2),
    dialerOutcome: 'Warm transfer',
    claimedBy: null,
    claimedByName: null,
    claimedAt: null,
    loanCreated: false,
    releaseCount: 0,
  },
  {
    id: 'RT-2006',
    name: 'Sofia Delgado',
    phone: '(555) 118-6642',
    email: 'sdelgado@example.com',
    loanAmount: 275000,
    loanPurpose: 'Purchase',
    state: 'NV',
    sourceId: 'zillow',
    lastContactedAt: hoursAgo(8),
    dialerOutcome: 'Successful contact',
    claimedBy: null,
    claimedByName: null,
    claimedAt: null,
    loanCreated: false,
    releaseCount: 2,
  },
  {
    id: 'RT-2007',
    name: 'Kevin Ross',
    phone: '(555) 447-2093',
    email: 'kross@example.com',
    loanAmount: 199000,
    loanPurpose: 'Refinance',
    state: 'OH',
    sourceId: 'facebook',
    lastContactedAt: hoursAgo(3.5),
    dialerOutcome: 'Requested callback',
    // Claim window already lapsed with no loan - falls back to the queue.
    claimedBy: 'officer3',
    claimedByName: 'Mike Specialist',
    claimedAt: hoursAgo(26),
    loanCreated: false,
    releaseCount: 0,
  },
  {
    id: 'RT-2008',
    name: 'Hannah Liu',
    phone: '(555) 330-8876',
    email: 'hliu@example.com',
    loanAmount: 720000,
    loanPurpose: 'Purchase',
    state: 'CA',
    sourceId: 'five9-transfer',
    lastContactedAt: hoursAgo(0.1),
    dialerOutcome: 'Warm transfer',
    claimedBy: null,
    claimedByName: null,
    claimedAt: null,
    loanCreated: false,
    releaseCount: 0,
  },
  {
    id: 'RT-2009',
    name: 'Marcus Feld',
    phone: '(555) 991-4432',
    email: 'mfeld@example.com',
    loanAmount: 355000,
    loanPurpose: 'Refinance',
    state: 'CO',
    sourceId: 'lendingtree',
    lastContactedAt: hoursAgo(12),
    dialerOutcome: 'Successful contact',
    claimedBy: 'officer1',
    claimedByName: 'John Officer',
    claimedAt: hoursAgo(6),
    loanCreated: true,
    releaseCount: 0,
  },
  {
    id: 'RT-2010',
    name: 'Alicia Torres',
    phone: '(555) 505-1276',
    email: 'atorres@example.com',
    loanAmount: 468000,
    loanPurpose: 'Purchase',
    state: 'WA',
    sourceId: 'website',
    lastContactedAt: hoursAgo(1.8),
    dialerOutcome: 'Successful contact',
    claimedBy: null,
    claimedByName: null,
    claimedAt: null,
    loanCreated: false,
    releaseCount: 0,
  },
]

export const getSource = (sourceId: string) =>
  LEAD_SOURCES.find((s) => s.id === sourceId)

/** Epoch ms at which an active claim lapses. */
export const claimExpiresAt = (lead: RealtimeLead) =>
  lead.claimedAt
    ? new Date(lead.claimedAt).getTime() + CLAIM_WINDOW_HOURS * 3600 * 1000
    : null

/**
 * A claim only counts while it is inside the window. Once it lapses without a
 * loan, the lead is treated as available again - no background job required.
 */
export function hasActiveClaim(lead: RealtimeLead, now: number) {
  if (!lead.claimedBy || !lead.claimedAt) return false
  if (lead.loanCreated) return true
  const expiry = claimExpiresAt(lead)
  return expiry !== null && expiry > now
}

export function getLeadState(
  lead: RealtimeLead,
  now: number,
  currentUserId: string,
): LeadState {
  if (lead.loanCreated) return 'loan_created'
  if (!hasActiveClaim(lead, now)) return 'available'
  return lead.claimedBy === currentUserId ? 'claimed_by_me' : 'claimed_by_other'
}

/** Milliseconds left on an active claim (0 when none / lapsed). */
export function claimTimeRemaining(lead: RealtimeLead, now: number) {
  const expiry = claimExpiresAt(lead)
  if (expiry === null) return 0
  return Math.max(0, expiry - now)
}

/** Render a duration in ms as "3h 12m" / "14m". */
export function formatDuration(ms: number) {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

/** How long ago the external dialer last made contact. */
export const timeSinceContact = (lead: RealtimeLead, now: number) =>
  now - new Date(lead.lastContactedAt).getTime()
